import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/types";
import { WorkAgreementField } from "permissions/resources/work/types";
import { z } from "zod";

export type WorkAgreementAccessJSON = z.infer<typeof WorkAgreementAccessSchema.WorkAgreementAccessJSONZ>;

export type WorkAgreementAccessParams = z.infer<typeof WorkAgreementAccessSchema.WorkAgreementAccessZ>;

export default class WorkAgreementAccessSchema {
  public static readonly WorkAgreementAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.WorkAgreementAccess),
    workAgreementId: z.uuid().nullish(),
    fields: z.record(z.enum(WorkAgreementField), z.enum(AccessLevel)).nullish(),
  });

  public static readonly WorkAgreementAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.WorkAgreementAccess),
    workAgreementId: z.instanceof(Guid).optional(),
    fields: z.record(z.enum(WorkAgreementField), z.enum(AccessLevel)).optional(),
  });

  public static validate(values: unknown): WorkAgreementAccessParams {
    return parseOrThrow(WorkAgreementAccessSchema.WorkAgreementAccessZ, values, {
      logError: true,
      schemaName: "WorkAgreementAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): WorkAgreementAccessJSON {
    return parseOrThrow(WorkAgreementAccessSchema.WorkAgreementAccessJSONZ, values, {
      logError: true,
      schemaName: "WorkAgreementAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is WorkAgreementAccessParams {
    try {
      WorkAgreementAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is WorkAgreementAccessJSON {
    try {
      WorkAgreementAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: WorkAgreementAccessJSON): WorkAgreementAccessParams {
    const parsed = parseOrThrow(WorkAgreementAccessSchema.WorkAgreementAccessJSONZ, data, {
      logError: true,
      schemaName: "WorkAgreementAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return WorkAgreementAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: WorkAgreementAccessParams): WorkAgreementAccessJSON {
    const parsed = WorkAgreementAccessSchema.validate(notification);
    return WorkAgreementAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    WorkAgreementAccessSchema.WorkAgreementAccessJSONZ,
    WorkAgreementAccessSchema.WorkAgreementAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.WorkAgreementAccess as const,
        workAgreementId: Guid.fromJSON(data.workAgreementId) ?? undefined,
        fields: (() => {
          if (!data.fields) return;
          let fieldsObj: Record<WorkAgreementField, AccessLevel> = {} as Record<WorkAgreementField, AccessLevel>;
          for (const [key, value] of Object.entries(data.fields ?? {})) {
            // Only accept exact enum member names (and not numeric reverse-mapping keys)
            if (Number.isNaN(Number(key)) && key in WorkAgreementField) {
              const field = WorkAgreementField[key as keyof typeof WorkAgreementField] as unknown as WorkAgreementField;
              const level = AccessLevel[value.toString() as keyof typeof AccessLevel];
              if (level) {
                fieldsObj[field] = value;
              } else {
                console.warn(`Unknown access level in WorkAgreementAccess: ${value}`);
              }
            } else {
              console.warn(`Unknown field in WorkAgreementAccess: ${key}`);
            }
          }
          return fieldsObj;
        })(),
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.WorkAgreementAccess as const,
        workAgreementId: resource.workAgreementId?.toJSON(),
        fields: (() => {
          if (!resource.fields) return;
          let fieldsObj: Record<WorkAgreementField, AccessLevel> = {} as Record<WorkAgreementField, AccessLevel>;
          for (const [key, value] of Object.entries(resource.fields)) {
            fieldsObj[key as unknown as WorkAgreementField] = value;
          }
          return fieldsObj;
        })(),
      }),
    }
  );
}
